<template>
    <primevue-multi-select
        ref="primevueMultiSelectRef"
        v-bind="props"
        :auto-filter-focus="props.filter"
        @update:model-value="emit('update:modelValue', $event)"
        @change="emit('change', $event)"
        @show="onShow"
        @hide="onHide"
        @focus="emit('focus')"
        @blur="emit('blur')"
        @before-show="emit('before-show')"
        @before-hide="emit('before-hide')"
        @filter="emit('filter')"
        @selectall-change="setAll($event)"
    >
        <!-- Pass through all slots except value (handled below) -->
        <template v-for="slot in passthroughSlots" v-slot:[slot]="scope" :key="slot">
            <slot :name="slot" v-bind="scope" />
        </template>

        <!-- Summary overrides consumer #value when over threshold and closed -->
        <template v-if="showSummaryValue" #value>
            <span v-tooltip.top="{ value: summaryTooltip, showDelay: 1000 }">{{ summary }}</span>
        </template>
        <template v-else-if="slots.value" #value="scope">
            <slot name="value" v-bind="scope" />
        </template>

        <!-- Default chipicon slot if not provided -->
        <template v-if="!slots.chipicon && props.display === 'chip'" #chipicon="{ removeCallback }">
            <font-awesome-icon
                class="multiselect-close-button"
                :icon="['fas', 'times']"
                @click.stop="removeCallback"
            />
        </template>

        <!-- Default dropdownicon slot if not provided -->
        <template v-if="!slots.dropdownicon" #dropdownicon>
            <font-awesome-icon
                class="dropdown-icon"
                :class="{ inverted: isDropdownOpen }"
                :icon="['fas', 'caret-down']"
            />
        </template>
    </primevue-multi-select>
</template>

<script lang="ts" setup generic="T">
import { computed, ref, useSlots } from 'vue';
import PrimevueMultiSelect, { MultiSelectAllChangeEvent, MultiSelectChangeEvent, MultiSelectProps } from 'primevue/multiselect';

interface SparkMultiSelectProps extends Omit<MultiSelectProps, 'modelValue'> {
    modelValue: Array<T>;
    summaryDisplayThreshold?: number;
    summaryLabel?: string;
    optionValue?: string | undefined;
}

const props = withDefaults(defineProps<SparkMultiSelectProps>(), {
    display: 'chip',
    filter: true,
    placeholder: 'Select options',
    scrollHeight: '300px',
    resetFilterOnHide: true,
    summaryDisplayThreshold: 1,
    summaryLabel: 'option',
    optionValue: undefined,
});

const emit = defineEmits<{
    'update:modelValue': [Array<T>];
    'change': [MultiSelectChangeEvent];
    'show': [];
    'hide': [];
    'focus': [];
    'blur': [];
    'before-show': [];
    'before-hide': [];
    'filter': [];
}>();

const slots = useSlots();

const isDropdownOpen = ref<boolean>(false);
const primevueMultiSelectRef = ref<InstanceType<typeof PrimevueMultiSelect> | null>(null);

const passthroughSlots = computed<Array<string>>(() => Object.keys(slots).filter((name: string) => name !== 'value'));
const summary = computed<string>(() => `${props.modelValue.length} ${props.summaryLabel}${props.modelValue.length === 1 ? '' : 's'} selected`);
const showSummaryValue = computed<boolean>(() => !isDropdownOpen.value && (props.modelValue?.length ?? 0) > props.summaryDisplayThreshold);

const summaryTooltip = computed<string>(() => {
    const selectedOptions = typeof props.modelValue[0] === 'object' ? props.modelValue.map((o: T) => o[props.optionLabel as keyof T] ?? null) : props.modelValue;
    return selectedOptions.filter(Boolean).join(', ');
});

function onShow (): void {
    isDropdownOpen.value = true;
    emit('show');
}

function onHide (): void {
    isDropdownOpen.value = false;
    emit('hide');
}

function setAll (event: MultiSelectAllChangeEvent): void {
    const isChecked = (event.originalEvent.currentTarget as unknown as { checked: boolean })?.checked; // the Primevue checked state is not correct here
    if (isChecked) {
        const optionValueProp = props.optionValue;
        const options = optionValueProp ? props.options?.map(option => option[optionValueProp]) : props.options;
        emit('update:modelValue', options ?? []);
    } else {
        emit('update:modelValue', []);
    }
}

function hide (): void {
    primevueMultiSelectRef.value?.hide();
}

defineExpose({ hide });

</script>

<style lang="scss" scoped>

.dropdown-icon {
    transition: transform 300ms ease;

    &.inverted {
        transform: rotate(180deg);
    }
}

</style>
